import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { ArrowRight, MapPin, Linkedin, Sparkles, Users, Globe, Calendar, Wine, Facebook, Instagram, Mail, TrendingUp } from "lucide-react";
import CompanyLogo from "@/components/CompanyLogo";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_wine-tech-hub/artifacts/8jo4w2ry_lawinetech-hero.png";
const HERO_FALLBACK = "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1600";

function formatDateShort(iso, lang = "fr") {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" });
}
function eventDateParts(iso) {
  const d = new Date(iso);
  return { day: d.getDate(), month: d.toLocaleDateString("fr", { month: "short" }).toUpperCase().replace(".", "") };
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => (url && url.startsWith("/api/")) ? `${BACKEND}${url}` : url;

export default function Home() {
  const { t, lang } = useLang();
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ members: 0, countries: 0, events_per_year: 0, poles: 0, social: { linkedin: 0, facebook: 0, instagram: 0, linkedin_url: "#", facebook_url: "#", instagram_url: "#" } });

  useEffect(() => {
    api.get("/news", { params: { limit: 3, lang } }).then((r) => setNews(r.data)).catch(() => {});
    api.get("/events", { params: { when: "upcoming", lang } }).then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/companies", { params: { limit: 8, lang } }).then((r) => setMembers(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [lang]);

  const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

  return (
    <PublicLayout>
      <Seo description="La WineTech connects startups, producers, technology providers, researchers, investors and ecosystem partners driving innovation across the global wine industry." path="/" />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-wine-100/60 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:py-24 lg:grid-cols-2">
          <div className="fade-in-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-wine-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-wine-700">
              <Sparkles className="h-3.5 w-3.5" />
              Innovate Together
            </span>
            <h1 className="mt-6 text-5xl font-bold uppercase leading-[1.05] tracking-tight text-navy md:text-6xl lg:text-7xl">
              {t("hero_title_pre")}<br />
              <span className="font-display normal-case italic text-wine-500" style={{ fontWeight: 500 }}>{t("hero_title_brand")}</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg text-neutral-600">{t("hero_subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/members"
                className="group inline-flex items-center gap-2 rounded-full bg-wine-500 px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wine-600 hover:shadow-lg"
                data-testid="home-cta-explore"
              >
                {t("cta_explore")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/events"
                className="inline-flex items-center gap-2 rounded-full border border-wine-300 bg-white px-7 py-3.5 text-sm font-semibold text-wine-700 transition-all hover:bg-wine-50"
                data-testid="home-cta-events"
              >
                {t("cta_see_events")}
              </Link>
            </div>
          </div>
          <div className="fade-in-up stagger-2 relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-wine-100 to-wine-50 blur-2xl" />
            <img
              src={HERO_IMG}
              onError={(e) => (e.target.src = HERO_FALLBACK)}
              alt="WineTech ecosystem"
              className="relative aspect-[4/3] w-full rounded-3xl object-cover shadow-xl"
              data-testid="home-hero-image"
            />
          </div>
        </div>
      </section>

      {/* Dynamic key stats */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-stats">
          {[
            { icon: Users, n: `${fmt(stats.members)}+`, l: "Membres actifs", testid: "stat-members" },
            { icon: Globe, n: fmt(stats.countries), l: "Pays représentés", testid: "stat-countries" },
            { icon: Calendar, n: fmt(stats.events_per_year), l: "Événements / an", testid: "stat-events" },
            { icon: Wine, n: fmt(stats.poles), l: "Pôles d'innovation", testid: "stat-poles" },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl border border-wine-100 bg-wine-50/30 p-5" data-testid={s.testid}>
              <s.icon className="h-6 w-6 text-wine-600" />
              <div className="mt-3 font-serif text-3xl font-semibold text-neutral-900">{s.n}</div>
              <div className="text-sm text-neutral-600">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* "La WineTech rayonne" — social followers (dynamic, ContentStudio or manual) */}
      <section className="relative overflow-hidden bg-gradient-to-br from-navy via-[#1a1f3a] to-[#0e1228] py-16 text-white" data-testid="home-social-section">
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-wine-500/20 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-wine-700/30 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-wine-300">Communauté</div>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl">La WineTech rayonne</h2>
          <p className="mt-3 max-w-2xl text-white/70">Rejoignez les milliers de professionnels qui suivent l&apos;actualité de l&apos;innovation viti-vinicole sur nos réseaux.</p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-social-grid">
            {[
              { icon: Linkedin, n: stats.social.linkedin, l: "LinkedIn", href: stats.social.linkedin_url, color: "bg-[#0A66C2]", testid: "social-linkedin", delta: stats.social.delta_week?.linkedin },
              { icon: Facebook, n: stats.social.facebook, l: "Facebook", href: stats.social.facebook_url, color: "bg-[#1877F2]", testid: "social-facebook", delta: stats.social.delta_week?.facebook },
              { icon: Instagram, n: stats.social.instagram, l: "Instagram", href: stats.social.instagram_url, color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]", testid: "social-instagram", delta: stats.social.delta_week?.instagram },
              { icon: Mail, n: stats.social.newsletter, l: "Newsletter", href: "#newsletter", color: "bg-gradient-to-br from-wine-500 to-wine-700", testid: "social-newsletter", delta: stats.social.delta_week?.newsletter },
            ].map((s) => {
              const Cmp = s.href === "#newsletter" ? "div" : "a";
              const linkProps = s.href === "#newsletter" ? {} : { href: s.href, target: "_blank", rel: "noreferrer" };
              const deltaPositive = Number(s.delta || 0) > 0;
              const deltaNegative = Number(s.delta || 0) < 0;
              return (
                <Cmp key={s.l} {...linkProps} className="group rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-md transition-all hover:bg-white/10 hover:-translate-y-1" data-testid={s.testid}>
                  <div className="flex items-start justify-between">
                    <div className={`inline-grid h-12 w-12 place-items-center rounded-xl ${s.color} text-white shadow-lg`}>
                      <s.icon className="h-6 w-6" />
                    </div>
                    {(deltaPositive || deltaNegative) && (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold ${deltaPositive ? "bg-emerald-400/20 text-emerald-300" : "bg-rose-400/20 text-rose-300"}`} data-testid={`${s.testid}-delta`}>
                        <TrendingUp className={`h-3 w-3 ${deltaNegative ? "rotate-180" : ""}`} />
                        {deltaPositive ? "+" : ""}{s.delta}
                      </span>
                    )}
                  </div>
                  <div className="mt-5 font-serif text-5xl font-semibold">{fmt(s.n)}</div>
                  <div className="mt-1 text-sm text-white/70">{s.l === "Newsletter" ? "Abonnés Newsletter" : `Abonnés ${s.l}`}</div>
                  {s.href !== "#newsletter" ? (
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-wine-300 transition-all group-hover:gap-2 group-hover:text-wine-200">
                      Suivre <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  ) : (
                    <div className="mt-4 text-xs text-white/50 italic">Mis à jour en temps réel</div>
                  )}
                </Cmp>
              );
            })}
          </div>
        </div>
      </section>

      {/* News */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="h-1 w-12 rounded-full bg-wine-500" />
            <h2 className="mt-3 text-3xl font-bold text-navy md:text-4xl">{t("actu")}</h2>
          </div>
          <Link to="/news" className="hidden items-center gap-1.5 text-sm font-semibold text-wine-600 hover:text-wine-700 md:inline-flex" data-testid="home-see-all-news">
            {t("see_all_news")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {news.map((n, i) => (
            <Link
              key={n.id}
              to={`/news/${n.slug}`}
              className={`fade-in-up stagger-${i+1} group block overflow-hidden rounded-2xl border border-neutral-100 bg-white transition-all hover:-translate-y-1 hover:shadow-lg`}
              data-testid={`home-news-card-${i}`}
            >
              <div className="aspect-[16/10] overflow-hidden bg-wine-50">
                <img src={n.image} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between text-xs">
                  <span className="rounded-full bg-wine-50 px-2.5 py-1 font-semibold uppercase tracking-wider text-wine-700">{n.category}</span>
                  <span className="text-neutral-500">{formatDateShort(n.published_at, lang)}</span>
                </div>
                <h3 className="mt-3 font-serif text-xl leading-snug text-neutral-900">{n.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-neutral-600">{n.excerpt}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-wine-600 group-hover:gap-2 transition-all">
                  {t("read_more")} <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Events */}
      <section className="bg-wine-50/30 py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <div className="h-1 w-12 rounded-full bg-wine-500" />
              <h2 className="mt-3 text-3xl font-bold text-navy md:text-4xl">{t("upcoming_events")}</h2>
            </div>
            <Link to="/events" className="hidden items-center gap-1.5 text-sm font-semibold text-wine-600 hover:text-wine-700 md:inline-flex" data-testid="home-see-all-events">
              {t("see_all_events")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
          {events.map((e, i) => {
              const { day, month } = eventDateParts(e.date);
              return (
                <Link
                  key={e.id}
                  to={`/events/${e.slug}`}
                  className="group flex gap-4 rounded-2xl border border-neutral-100 bg-white p-4 transition-all hover:-translate-y-1 hover:shadow-md"
                  data-testid={`home-event-card-${i}`}
                >
                  <div className="grid h-20 w-20 shrink-0 place-items-center rounded-xl bg-wine-50 text-wine-700">
                    <div className="text-center">
                      <div className="font-serif text-2xl font-semibold leading-none">{day}</div>
                      <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest">{month}</div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <h3 className="font-serif text-lg text-neutral-900">{e.title}</h3>
                      {e.audience === "members_only" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">🔒 Membres</span>}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-neutral-500">
                      <MapPin className="h-3 w-3" /> {e.location}
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm text-neutral-600">{e.description}</p>
                    <div className="mt-3 text-sm font-semibold text-wine-600">{t("learn_more")} →</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Members */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="h-1 w-12 rounded-full bg-wine-500" />
            <h2 className="mt-3 text-3xl font-bold text-navy md:text-4xl">{t("community_members")}</h2>
          </div>
          <Link to="/members" className="hidden items-center gap-1.5 text-sm font-semibold text-wine-600 hover:text-wine-700 md:inline-flex" data-testid="home-see-all-members">
            {t("see_all_members")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {members.slice(0, 4).map((m, i) => (
            <Link
              key={m.id}
              to={`/members/${m.slug}`}
              className="group rounded-2xl border border-neutral-100 bg-white p-5 text-center transition-all hover:-translate-y-1 hover:bg-wine-50/40 hover:shadow-md"
              data-testid={`home-member-card-${i}`}
            >
              <CompanyLogo
                src={m.logo}
                name={m.name}
                size={80}
                className="mx-auto rounded-full ring-4 ring-wine-50"
              />
              <h3 className="mt-3 font-serif text-base font-semibold text-neutral-900">{m.name}</h3>
              <p className="mt-1 text-xs text-neutral-500">{m.city}, {m.country}</p>
              <span className="mt-3 inline-block rounded-full bg-wine-50 px-2.5 py-1 text-xs font-semibold text-wine-700">{m.category}</span>
              <div className="mt-3 flex justify-center">
                <Linkedin className="h-4 w-4 text-neutral-400 group-hover:text-wine-600" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Join CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-wine-100 bg-gradient-to-br from-wine-50 to-white p-8 md:flex-row md:items-center md:p-10">
          <div className="max-w-xl">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-wine-500 text-white">
                <Sparkles className="h-6 w-6" />
              </div>
              <h3 className="text-2xl font-bold text-navy md:text-3xl">Rejoignez <span className="font-display italic text-wine-600" style={{ fontWeight: 500 }}>{t("comp_home_90")}</span></h3>
            </div>
            <p className="mt-3 text-neutral-600">{t("comp_home_91")}</p>
          </div>
          <Link to="/join" className="rounded-full bg-wine-500 px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wine-600 hover:shadow-lg" data-testid="home-cta-join-now">
            Rejoindre maintenant →
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
